import type { SelectableItem } from '~/models/CampaignModel';
import { CustomerType } from '~/models/CustomerType';
import { OrderVariant } from '~/models/OrderVariant';
import { IProductPrice } from '~/models/product/ProductPrice';
import { ProductType } from '~/models/product/ProductType';
import { noUndefined, onlyUnique } from '~/utils/arrays';

export const hasPremiums = (selectedProducts: SelectableItem[]) =>
  selectedProducts.filter((product) => product.productType === ProductType.Premium).length > 0;

const calculateCostFirstYear = (price: IProductPrice): number => {
  if (!price.isRecurring) {
    return price.price;
  }
  if (!price.offerPeriodInMonths && price.price === price.originalPrice) {
    return price.originalPrice * 12;
  }
  if (price.offerPeriodInMonths) {
    const monthsLeft = 12 - price.offerPeriodInMonths;
    return price.price * price.offerPeriodInMonths + price.originalPrice * monthsLeft;
  }
  return price.price * 12;
};

const originalCostFirstYear = (price: IProductPrice): number => {
  if (!price.isRecurring) {
    return price.originalPrice;
  }
  return price.originalPrice * 12;
};

export const calculateSavingsFirstYear = (price: IProductPrice): number => {
  return originalCostFirstYear(price) - calculateCostFirstYear(price);
};

const calculateCostIntervals = (price: IProductPrice, offerPeriodInMonths: number): number => {
  if (!price.isRecurring) {
    return price.price;
  }
  if (!price.offerPeriodInMonths && price.price === price.originalPrice) {
    return price.price;
  }
  if (price.offerPeriodInMonths) {
    if (price.offerPeriodInMonths > offerPeriodInMonths) {
      return price.price;
    }
    return price.originalPrice;
  }
  return price.price;
};

export const getOfferPeriods = (selectedProducts: SelectableItem[]) => {
  const offerPeriods = selectedProducts
    .map((product) => product.pricing.offerPeriodInMonths)
    .filter(onlyUnique)
    .filter(noUndefined);
  offerPeriods.sort((n1, n2) => n1 - n2);

  return offerPeriods;
};

export const getSummary = (products: SelectableItem[]) => {
  const recurring = products.filter((product) => product.pricing.isRecurring);
  const nonRecurring = products.filter((product) => !product.pricing.isRecurring);
  const offerPeriods = getOfferPeriods(products);

  const monthly = recurring.reduce((a, b) => a + b.pricing.price, 0);
  const costIntervals = [monthly];
  const costFirstYear = products.reduce((a, b) => a + calculateCostFirstYear(b.pricing), 0);
  const costNonRecurring = nonRecurring.reduce((n, price) => n + price.pricing.price, 0);

  offerPeriods.forEach((offerPeriodInMonths) =>
    costIntervals.push(recurring.reduce((a, b) => a + calculateCostIntervals(b.pricing, offerPeriodInMonths), 0)),
  );

  return {
    monthly,
    costIntervals,
    costFirstYear,
    costNonRecurring,
  };
};

/**
 * Model used for reporting events to GA
 */
export const toBasketProduct = (product: SelectableItem, customerType: CustomerType) => ({
  id: product.offer.commercialProductId,
  name: product.displayData.name,
  price: calculateCostFirstYear(product.pricing),
  category: product.productType,
  quantity: 1,
  variant: getTrackingProductVariant(product.productType, customerType),
});

const getTrackingProductVariant = (category: ProductType, customerType: CustomerType) => {
  if (category === ProductType.Subscription) {
    switch (customerType) {
      case CustomerType.Normal:
        return OrderVariant.Normal;
      case CustomerType.IpNormal:
        return OrderVariant.IpNormal;
      case CustomerType.IpNormalBRL:
        return OrderVariant.IpNormalBrl;
      default:
        return OrderVariant.Unknown;
    }
  }
  return undefined;
};
